/**
 * FeedbackUiService — owns the open/closed state of the Feedback widget
 * panel, so that triggers in different parts of the shell (the global
 * floating button, the settings rail entry, etc.) can all toggle the
 * same UI surface.
 *
 * RESPONSIBILITY: state only — does NOT render the panel itself. The
 * `PaperFeedbackWidgetComponent` reads from this service and renders the panel
 * wherever it is mounted in the shell.
 *
 * AI CONTEXT: in Tauri builds, the macOS Help → "Send Feedback..." menu
 * item emits a `menu:feedback` event from Rust. The service subscribes
 * once at construction so the menu opens the same panel as the floating
 * trigger.
 */
import { Injectable, OnDestroy, signal } from '@angular/core';
import { isTauri } from './../tauri-runtime';

@Injectable({ providedIn: 'root' })
export class FeedbackUiService implements OnDestroy {
  private readonly _isOpen = signal(false);
  private unlistenMenu: (() => void) | null = null;

  readonly isOpen = this._isOpen.asReadonly();

  constructor() {
    if (isTauri) {
      void this.subscribeToMenuEvent();
    }
  }

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
  }

  toggle(): void {
    this._isOpen.update(v => !v);
  }

  ngOnDestroy(): void {
    this.unlistenMenu?.();
    this.unlistenMenu = null;
  }

  private async subscribeToMenuEvent(): Promise<void> {
    try {
      const { listen } = await import('@tauri-apps/api/event');
      this.unlistenMenu = await listen('menu:feedback', () => this.open());
    } catch (err) {
      console.warn('[FeedbackUiService] menu:feedback subscription failed:', err);
    }
  }
}
